import { useState, useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation, useNavigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { 
  ChartLineUp, 
  Bell, 
  Gear, 
  ClockCounterClockwise,
  House,
  TrendUp,
  CaretRight,
  ChartBar,
  Wallet,
  BookOpen,
  Robot,
  ShieldCheck
} from "@phosphor-icons/react";

// Pages
import Dashboard from "@/pages/Dashboard";
import OpportunityDetail from "@/pages/OpportunityDetail";
import Settings from "@/pages/Settings";
import Alerts from "@/pages/Alerts";
import TradeHistory from "@/pages/TradeHistory";
import Backtesting from "@/pages/Backtesting";
import PaperTrading from "@/pages/PaperTrading";
import Methodology from "@/pages/Methodology";
import Bots from "@/pages/Bots";
import BotPerformance from "@/pages/BotPerformance";
import TradingJournal from "@/pages/TradingJournal";
import AdvancedStats from "@/pages/AdvancedStats";
import RulesAlerts from "@/pages/RulesAlerts";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

// Sidebar Component
const Sidebar = ({ alertCount }) => {
  const location = useLocation();
  const navigate = useNavigate();
  
  const navItems = [
    { path: "/", icon: House, label: "Dashboard" },
    { path: "/bots", icon: Robot, label: "Bots" },
    { path: "/bots/performance", icon: ChartBar, label: "Performance" },
    { path: "/journal", icon: BookOpen, label: "Journal DAX40" },
    { path: "/advanced-stats", icon: ChartBar, label: "Stats Avancées" },
    { path: "/rules", icon: ShieldCheck, label: "Règles & Alertes" },
    { path: "/paper", icon: Wallet, label: "Paper Trading" },
    { path: "/backtesting", icon: ChartBar, label: "Backtesting" },
    { path: "/alerts", icon: Bell, label: "Alertes", badge: alertCount },
    { path: "/history", icon: ClockCounterClockwise, label: "Historique" },
    { path: "/methodology", icon: BookOpen, label: "Methodologie" },
    { path: "/settings", icon: Gear, label: "Paramètres" },
  ];

  return (
    <aside className="sidebar" data-testid="sidebar">
      <div className="p-4 border-b border-slate-200">
        <div className="flex items-center gap-2">
          <ChartLineUp size={24} weight="bold" className="text-blue-600" />
          <span className="font-heading font-bold text-lg text-slate-900">ProTrader</span>
        </div>
        <p className="text-xs text-slate-500 mt-1 font-body">Scanner Expert</p>
      </div>
      
      <nav className="sidebar-nav">
        {navItems.map((item) => (
          <button
            key={item.path}
            onClick={() => navigate(item.path)}
            className={`nav-item w-full relative ${location.pathname === item.path ? 'active' : ''}`}
            data-testid={`nav-${item.label.toLowerCase()}`}
          >
            <item.icon size={20} weight={location.pathname === item.path ? "fill" : "regular"} />
            <span>{item.label}</span>
            {item.badge > 0 && (
              <span className="alert-badge">{item.badge > 9 ? '9+' : item.badge}</span>
            )}
          </button>
        ))}
      </nav>
      
      <div className="p-4 border-t border-slate-200">
        <div className="flex items-center gap-2 text-xs text-slate-400">
          <TrendUp size={14} />
          <span>v1.0.0</span>
        </div>
      </div>
    </aside>
  );
};

// Main Layout
const Layout = ({ children, alertCount }) => {
  return (
    <div className="app-container">
      <Sidebar alertCount={alertCount} />
      <main className="main-content">
        {children}
      </main>
      <Toaster position="top-right" richColors />
    </div>
  );
};

function AppContent() {
  const [alertCount, setAlertCount] = useState(0);

  // Request browser notification permission
  useEffect(() => {
    if ('Notification' in window && Notification.permission === 'default') {
      // Ask after 5 seconds to not be intrusive
      const timer = setTimeout(() => {
        Notification.requestPermission();
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, []);

  useEffect(() => {
    let prevCount = 0;
    
    const fetchAlertCount = async () => {
      try {
        const response = await fetch(`${API}/alerts?unread_only=true`);
        const data = await response.json();
        const newCount = data.length;
        
        // Send browser notification for new alerts
        if (newCount > prevCount && prevCount > 0 && 'Notification' in window && Notification.permission === 'granted') {
          const newAlerts = data.slice(0, newCount - prevCount);
          for (const alert of newAlerts) {
            new Notification(`ProTrader - ${alert.symbol}`, {
              body: alert.message,
              icon: '/favicon.ico',
              tag: alert.id
            });
          }
        }
        
        prevCount = newCount;
        setAlertCount(newCount);
      } catch (error) {
        console.error("Error fetching alerts:", error);
      }
    };
    
    fetchAlertCount();
    const interval = setInterval(fetchAlertCount, 15000);
    return () => clearInterval(interval);
  }, []);

  return (
    <Layout alertCount={alertCount}>
      <Routes>
        <Route path="/" element={<Dashboard />} />
        <Route path="/bots" element={<Bots />} />
        <Route path="/bots/performance" element={<BotPerformance />} />
        <Route path="/journal" element={<TradingJournal />} />
        <Route path="/advanced-stats" element={<AdvancedStats />} />
        <Route path="/rules" element={<RulesAlerts />} />
        <Route path="/opportunity/:symbol" element={<OpportunityDetail />} />
        <Route path="/paper" element={<PaperTrading />} />
        <Route path="/backtesting" element={<Backtesting />} />
        <Route path="/alerts" element={<Alerts />} />
        <Route path="/history" element={<TradeHistory />} />
        <Route path="/methodology" element={<Methodology />} />
        <Route path="/settings" element={<Settings />} />
      </Routes>
    </Layout>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AppContent />
    </BrowserRouter>
  );
}

export default App;
